import { useState, type Ref } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2 } from "lucide-react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { keepNow, type NowLinkKind, type NowSessionDto } from "../../api/now";
import { followUpTarget } from "../../lib/nowModes";
import { stageMmOpen } from "../../lib/mmHandoff";
import { stageWorkspaceItem } from "../../lib/workspaceHandoff";
import { queryClient } from "../../lib/queryClient";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

// Same keys CaptureSheet invalidates after filing, plus the Now step list.
const INVALIDATE_KEYS = ["home-today-actions", "lifeos-roots", "cockpit-projects", "now-today"];

interface Props {
  session: NowSessionDto;
  onClose: () => void;
  headingRef?: Ref<HTMLHeadingElement>;
}

/** "For today" — the same closing screen for every path (spec 2026-11-04 §2.5). */
const NowTake = ({ session, onClose, headingRef }: Props) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const [stepText, setStepText] = useState(session.stepText ?? "");
  const [createToo, setCreateToo] = useState(false);
  const [kept, setKept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [keepError, setKeepError] = useState(false);

  // Kinds from the tool, then the brought-in items by their own labels (never stored as text).
  const brought = session.broughtIn
    .map((b) => session.connections.find((c) => c.kind === b.kind && c.id === b.id)?.label)
    .filter((label): label is string => Boolean(label));
  const changed = [
    ...session.changed.map((c) => t(`now.take.changedKind.${c.kind}`, c.count ? { count: c.count } : undefined)),
    ...brought.map((label) => t("now.take.used", { label })),
  ];

  const keep = async () => {
    if (stepText.trim().length < 3) return;
    setBusy(true);
    setKeepError(false);
    try {
      await keepNow(retryFetch, session._id, { stepText: stepText.trim(), ...(createToo ? { create: true } : {}) });
      setKept(true);
      INVALIDATE_KEYS.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
    } catch {
      setKeepError(true);
    } finally {
      setBusy(false);
    }
  };

  const openFollowUp = (kind: NowLinkKind, id: string) => {
    const target = followUpTarget(kind, id);
    if (target.mmId) stageMmOpen(target.mmId);
    if (target.workspace) stageWorkspaceItem(target.workspace.section, target.workspace.id);
    onClose();
    navigate(target.path);
  };

  return (
    <section className="space-y-4">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-foreground focus:outline-none">
        {t("now.take.title")}
      </h2>
      <div className="space-y-2">
        <Textarea
          id="now-step"
          aria-label={t("now.take.title")}
          dir="auto"
          value={stepText}
          onChange={(e) => setStepText(e.target.value)}
          rows={2}
          maxLength={300}
          disabled={kept}
        />
        {session.proposal && !kept && (
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input type="checkbox" checked={createToo} onChange={(e) => setCreateToo(e.target.checked)} />
            {t("now.proposal", { title: session.proposal.title, kind: t(`now.proposalKind.${session.proposal.kind}`) })}
          </label>
        )}
        {kept ? (
          <p className="text-sm text-primary" role="status">{t("now.kept")}</p>
        ) : (
          <div className="flex justify-end">
            <Button className="rounded-full min-h-11" disabled={busy || stepText.trim().length < 3} onClick={keep}>
              {busy && <Loader2 className="me-1.5 h-4 w-4 motion-safe:animate-spin" aria-hidden />}
              {t("now.take.keep")}
            </Button>
          </div>
        )}
        {keepError && !kept && <p className="text-sm text-destructive" role="alert">{t("now.error")}</p>}
      </div>

      {session.wisdomLine && (
        <p dir="auto" className="font-serif italic text-[17px] leading-relaxed text-foreground/90">{session.wisdomLine}</p>
      )}

      {changed.length > 0 && (
        <p role="status" className="text-sm text-foreground/80">{t("now.take.changed", { list: changed.join(", ") })}</p>
      )}

      {session.followUps.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-widest text-foreground/70">{t("now.followUps")}</p>
          {session.followUps.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => openFollowUp(f.kind, f.id)}
              className="flex min-h-11 w-full items-center justify-between rounded-xl px-2 text-start text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span dir="auto">{f.label}</span>
              <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </button>
          ))}
        </div>
      )}

      {kept && <p className="text-sm text-foreground/80">{t("now.take.tonight")}</p>}
      <div className="flex justify-end">
        <Button variant="ghost" className="min-h-11" onClick={onClose}>{t("now.done")}</Button>
      </div>
    </section>
  );
};

export default NowTake;
